import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";
import { Upload } from "antd";

import { materialCategoryOptions } from "utils/data";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import Tooltip from "@mui/material/Tooltip";

const CreateUWMaterialModal = ({
  visible,
  handleClose,
  handleOk,
  isEdit = false,
  material = {},
  handleRefresh = () => {},
  handleAfterOk = () => {},
  refreshDelay = 0,
  readOnly = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState();
  const [editMode, setEditMode] = useState(isEdit);
  const [readOnlyMode, setReadOnlyMode] = useState(readOnly);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (isEdit || readOnly) {
      const cate = materialCategoryOptions.find((x) => x.value == material?.category);

      let resetPayload = {
        name: material?.name,
        category: cate ? cate : { label: material?.category, value: material?.category },
        code: material?.code,
        description: material?.metadata?.description,
        fabrication: material?.metadata?.fabrication,
        vendor: material?.metadata?.vendor,
        made_in_usa: material?.metadata?.made_in_usa,
        availability: material?.metadata?.availability,
        standard_cost: material?.metadata?.standard_cost,

        uom: material?.metadata?.uom,

        yield_factor: material?.metadata?.yield_factor,

        color_code: material?.metadata?.color_code,

        color_names: material?.metadata?.color_names,
      };

      if (material?.metadata?.images && material?.metadata?.images.length > 0) {
        setImages(material?.metadata?.images);
        setSelectedImage(material.metadata?.images[0]);
      }

      reset(resetPayload);
    } else {
      reset({});
    }
  }, [isEdit, readOnly]);

  const handleCreateMaterial = async (data) => {
    console.log(data);
    try {
      setUploading(true);
      const attachFiles = await handleUploadFiles(images, {}, "material");
      console.log("saving");
      let payload = {
        name: data["name"],
        category: data["category"]?.value,
        code: data["code"],
        metadata: {
          ...material?.metadata,
          description: data["description"],
          images: attachFiles,
          fabrication: data?.fabrication,
          vendor: data?.vendor,
          made_in_usa: data?.made_in_usa,
          availability: data?.availability,
          standard_cost: data?.standard_cost,

          uom: data?.uom,

          yield_factor: data?.yield_factor,

          color_code: data?.color_code,

          color_names: data?.color_names,
        },
      };

      if (editMode) {
        if (!material.id) {
          setUploading(false);
          return toast.error("Can't find the material!");
        }
        console.log(material.id, payload);
        const newCreatedMaterial = await api.materialLibrary.update(material.id, payload);
        setUploading(false);

        toast.success(`${data["name"]} has been saved successfully!`);
        handleClose();

        handleAfterOk(newCreatedMaterial);
      } else {
        const newCreatedMaterial = await api.materialLibrary.create(payload);
        setUploading(false);

        toast.success(`${data["name"]} has been saved successfully!`);
        handleClose();
        handleAfterOk(newCreatedMaterial);
      }

      setTimeout(() => {
        handleRefresh();
      }, refreshDelay);
    } catch (err) {
      console.log(err);
      setUploading(false);

      toast.error(`Material could not be saved!`);
    }
  };

  const handleUploadImages = async (data) => {
    const file = data.file;
    const image = Object.assign(file, {
      preview: URL.createObjectURL(file),
    });
    setSelectedImage(image);
    setImages((prevState) => [...prevState, image]);

    // return api.uploads.create([file])
  };

  const FormFieldInput = ({ label, name, required = false }) => (
    <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
        <SuiTypography
          component="label"
          variant="caption"
          fontWeight="bold"
          textTransform="capitalize"
          required={required}
        >
          {label}
        </SuiTypography>
      </SuiBox>
      <SuiInput placeholder={label} {...register(name)} inputProps={{ required: required }} />
    </SuiBox>
  );

  return (
    <Dialog
      open={visible}
      onClose={(e, reason) => {
        if (reason != "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="lg"
      fullWidth={true}
    >
      <Backdrop sx={{ material: "info", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress material="info" sx={{ mr: 1 }} size={32} />
        <SuiTypography variant="h6" fontWeight="medium" material="info">
          Uploading files...
        </SuiTypography>
      </Backdrop>
      <DialogTitle id="alert-dialog-title">
        <SuiBox display="flex" gap={1}>
          {readOnlyMode ? "Detail" : editMode ? "Edit Material" : "Create Material"}
          {readOnlyMode ? (
            <Tooltip title={`Edit Material`}>
              <IconButton
                aria-label="edit"
                onClick={() => {
                  setReadOnlyMode(false);
                  setEditMode(true);
                }}
                size="small"
                sx={{ "&:hover": { backgroundColor: (theme) => theme.palette.grey[200] } }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </SuiBox>

        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form
        onSubmit={handleSubmit(handleOk ? handleOk : handleCreateMaterial)}
        style={{ overflow: "auto", maxHeight: "600px" }}
      >
        <fieldset disabled={readOnlyMode}>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={5}>
                  <SuiBox>
                    <SuiBox ml={0.5}>
                      <SuiBox display="flex" justifyContent="space-between" gap={1}>
                        <SuiTypography
                          variant="button"
                          fontWeight="bold"
                          textTransform="capitalize"
                        >
                          Material Image
                        </SuiTypography>
                        <SuiBox>
                          {readOnlyMode ? null : (
                            <Upload
                              name="material-images-button"
                              showUploadList={false}
                              customRequest={handleUploadImages}
                              accept="image/*"
                              className="rounded-lg h-full"
                              multiple
                            >
                              <SuiButton
                                variant="text"
                                sx={{ textTransform: "capitalize" }}
                                color="info"
                              >
                                <UploadIcon />
                                Upload
                              </SuiButton>
                            </Upload>
                          )}
                        </SuiBox>
                      </SuiBox>
                    </SuiBox>
                    <SuiBox px={4} py={1}>
                      {selectedImage ? (
                        <ImageCardWrapper
                          data={selectedImage}
                          width={300}
                          height={300}
                          sx={{
                            borderRadius: "md",
                          }}
                        />
                      ) : (
                        <Upload.Dragger
                          name="material-images"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          accept="image/*"
                          className="rounded-lg h-full"
                          multiple
                        >
                          <SuiBox
                            sx={{
                              overflow: "hidden",
                              width: "100%",
                              backgroundImage: ({
                                functions: { rgba, linearGradient },
                                palette: { gradients },
                              }) =>
                                `${linearGradient(
                                  rgba(gradients.secondary.main, 0.2),
                                  rgba(gradients.secondary.state, 0.4)
                                )}`,
                              height: "300px",

                              margin: "-16px 0px",
                              boxShadow: ({ boxShadows: { md } }) => md,
                              objectFit: "contain",
                              objectPosition: "center",
                            }}
                            onClick={() => console.log("click")}
                            display="flex"
                            alignItems="center"
                            position="relative"
                            minHeight="300px"
                            borderRadius="md"
                          />
                        </Upload.Dragger>
                      )}

                      <SuiBox
                        sx={{ mt: 1, overflow: "auto" }}
                        width={"100%"}
                        display="flex"
                        flexWrap="noWrap"
                        gap={1}
                      >
                        {images.map((img) => (
                          <SuiBox sx={{ cursor: "pointer" }} onClick={() => setSelectedImage(img)}>
                            <ImageCardWrapper data={img} width={50} height={50} />
                          </SuiBox>
                        ))}
                      </SuiBox>
                    </SuiBox>
                  </SuiBox>
                </Grid>

                <Grid item xs={12} sm={12} md={7}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={8}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            required
                            textTransform="capitalize"
                          >
                            Material Name
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput
                          placeholder="Material Name"
                          required
                          inputProps={{ maxLength: 200 }}
                          {...register("name", { required: "A material name is required!" })}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                            required
                          >
                            Category
                          </SuiTypography>
                        </SuiBox>

                        <Controller
                          placeholder="Category"
                          name="category"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <SuiSelect
                              {...field}
                              options={materialCategoryOptions}
                              isDisabled={readOnlyMode}
                            />
                          )}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Code" name="code" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Fabrication" name="fabrication" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Vendor" name="vendor" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Made in USA" name="made_in_usa" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Availability" name="availability" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Standard Cost" name="standard_cost" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="UOM" name="uom" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Yield Factor" name="yield_factor" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Color Code" name="color_code" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Color Names" name="color_names" />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Description
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput
                          placeholder="Add a new event to your Calendar"
                          rows={4}
                          multiline={true}
                          fullWidth={true}
                          inputProps={{ maxLength: 2000 }}
                          {...register(`description`)}
                        />
                      </SuiBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <SuiBox
                pt={3}
                px={3}
                display={readOnlyMode ? "none" : "flex"}
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                      {editMode ? "Save" : "Create"}
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </fieldset>
      </form>
    </Dialog>
  );
};

export default CreateUWMaterialModal;
